import { useEffect, useState } from "react";
import mermaid from "mermaid";

let mermaidInitialized = false;
function ensureMermaid() {
  if (mermaidInitialized) return;
  mermaid.initialize({
    startOnLoad: false,
    // 'strict' (Mermaid's default) HTML-escapes every node label and rejects
    // <script> in the input source. Combined with the fact that our Mermaid
    // source is built entirely server-side from typed SQL data (no raw user
    // input is concatenated into it), this is the actual XSS boundary for
    // the diagram. We tried wrapping the output SVG in DOMPurify as a
    // defense-in-depth layer but DOMPurify's <foreignObject> namespace
    // handling strips Mermaid's HTML label content, which would silently
    // break every diagram. Verdict: rely on `securityLevel: 'strict'` +
    // server-side input control; DO NOT inject untrusted Mermaid source
    // here from any caller.
    securityLevel: "strict",
    theme: "base",
    fontFamily: '"IBM Plex Sans", sans-serif',
    themeVariables: {
      primaryColor: "#f4f4f4",
      primaryTextColor: "#161616",
      primaryBorderColor: "#0f62fe",
      lineColor: "#525252",
      secondaryColor: "#e8e8e8",
      tertiaryColor: "#ffffff",
      background: "#ffffff",
      fontFamily: '"IBM Plex Sans", sans-serif',
    },
    flowchart: { curve: "linear", htmlLabels: true },
  });
  mermaidInitialized = true;
}

// Heuristic to avoid rendering an obviously incomplete mermaid block during streaming.
function isLikelyComplete(code) {
  if (!code || code.trim().length < 12) return false;
  const trimmed = code.trim();
  const lastLine = trimmed.split(/\r?\n/).pop().trim();
  if (!lastLine) return false;
  // Unfinished arrow / pipe at end of last line = still streaming
  if (/(-{1,3}>|--|-{1,2}|\|)$/.test(lastLine)) return false;
  return true;
}

let idCounter = 0;

export default function MermaidDiagram({ code, bare = false }) {
  const [svgHtml, setSvgHtml] = useState("");
  const [err, setErr] = useState(null);

  useEffect(() => {
    ensureMermaid();
    if (!isLikelyComplete(code)) {
      // While streaming, keep the previously-rendered SVG visible (don't flash placeholder
      // on every chunk). Only show placeholder when we have NOTHING rendered yet.
      return;
    }

    // Debounce: re-render at most ~3x/sec; once at the end of streaming.
    let cancelled = false;
    const handle = setTimeout(async () => {
      try {
        idCounter += 1;
        const id = `mmd-${idCounter}-${Math.random().toString(36).slice(2, 8)}`;
        const { svg } = await mermaid.render(id, code);
        // See `securityLevel: 'strict'` in ensureMermaid() above — that's
        // the actual XSS defense for this surface. We do NOT wrap with
        // DOMPurify here because its <foreignObject> namespace handling
        // strips Mermaid's HTML node labels (verified — produces empty
        // boxes). The Mermaid source is generated server-side from typed
        // SQL queries; no untrusted caller should pass into <MermaidDiagram>.
        if (!cancelled) {
          setSvgHtml(svg);
          setErr(null);
        }
      } catch (e) {
        if (!cancelled) setErr(e?.message || "Mermaid render failed");
      }
    }, 350);

    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [code]);

  if (err) {
    return (
      <div className="border border-[var(--ml-error)]/30 bg-[var(--ml-error)]/5 p-3 my-3" data-testid="mermaid-error">
        <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-error)] mb-1">
          Mermaid · render failed
        </div>
        <pre className="text-xs text-[var(--ml-text-secondary)] whitespace-pre-wrap m-0">{code}</pre>
      </div>
    );
  }

  if (!svgHtml) {
    return (
      <div
        className="ml-card p-4 my-3 bg-white text-xs text-[var(--ml-text-secondary)] flex items-center gap-2"
        data-testid="mermaid-pending"
      >
        <span className="ml-dot" /><span className="ml-dot" /><span className="ml-dot" />
        <span className="ml-mono uppercase tracking-widest">Rendering diagram…</span>
      </div>
    );
  }

  return (
    <div
      className={
        bare
          ? "inline-block"
          : "ml-card p-4 my-3 overflow-x-auto bg-white flex justify-center"
      }
      data-testid="mermaid-diagram"
      dangerouslySetInnerHTML={{ __html: svgHtml }}
    />
  );
}
